import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { DetailGrid } from '@/components/backoffice/DetailGrid'
import { StageBadge } from '@/components/shared/StageBadge'
import { PaymentSummary } from '@/components/shared/PaymentSummary'
import { DocumentSection } from '@/components/shared/DocumentSection'
import { CalendarEventsSection } from '@/components/shared/CalendarEventsSection'
import { formatDate } from '@/lib/utils'

type DocumentAttachment = {
  id: string
  name: string
  file_url: string | null
  created_at: string
}

type Payment = {
  id: string
  service_fee_cents: number
  gov_fee_cents: number
  total_cents: number
  paid: boolean
  paid_at: string | null
  invoice_number: string | null
  receipt_number: string | null
  refunded: boolean
  refund_amount_cents: number | null
}

type CalendarEvent = {
  id: string
  title: string
  due_date: string
  source: string
  completed: boolean
  complete_path?: string
  delete_path?: string
}

type Registration = {
  id: string
  stage: string
  type_name: string
  type_code: string
  jurisdiction_label: string | null
  business_name: string
  account_name: string
  created_at: string
  payment: Payment | null
  filing: { submitted_at: string | null; accepted: boolean; rejected: boolean } | null
  completion: { created_at: string } | null
  application_documents: DocumentAttachment[]
  registration_documents: DocumentAttachment[]
  completion_documents: DocumentAttachment[]
}

type Props = {
  registration: Registration
  calendar_events: CalendarEvent[]
}

export default function BackofficeTaxRegistrationsShow({ registration, calendar_events }: Props) {
  return (
    <BackofficeLayout
      title={registration.type_name}
      breadcrumbs={[
        { label: 'Backoffice', href: '/backoffice/dashboard' },
        { label: 'Tax Registrations', href: '/backoffice/tax_registrations' },
        { label: registration.type_name },
      ]}
    >
      <div className="space-y-6">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-xl font-semibold tracking-tight">{registration.type_name}</h1>
            <StageBadge stage={registration.stage} variant="compliance" />
          </div>
          <p className="text-sm text-muted-foreground">{registration.business_name} · {registration.account_name}</p>
        </div>

        <DetailGrid items={[
          { label: 'Type', value: `${registration.type_name} (${registration.type_code})` },
          { label: 'Jurisdiction', value: registration.jurisdiction_label || '—' },
          { label: 'Created', value: formatDate(registration.created_at) },
        ]} />

        {/* Payment */}
        <PaymentSummary
          payment={registration.payment}
          adminActions={registration.payment ? {
            invoicePath: `/backoffice/tax_registrations/${registration.id}/payments/${registration.payment.id}/invoice`,
            confirmPaymentPath: `/backoffice/tax_registrations/${registration.id}/payments/${registration.payment.id}/payment_confirmation`,
            refundPath: `/backoffice/tax_registrations/${registration.id}/payments/${registration.payment.id}/refund/new`,
          } : undefined}
        />

        {/* Filing */}
        <div>
          <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-2">Filing</p>
          {registration.filing ? (
            <div className="border rounded-lg px-4 py-3 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Submitted</span>
                <span>{registration.filing.submitted_at ? formatDate(registration.filing.submitted_at) : '—'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Status</span>
                <span>{registration.filing.accepted ? 'Accepted' : registration.filing.rejected ? 'Rejected' : 'Pending'}</span>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No filing submitted</p>
          )}
        </div>

        {/* Completion */}
        {registration.completion && (
          <div>
            <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-2">Completion</p>
            <div className="border rounded-lg px-4 py-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Completed</span>
                <span>{formatDate(registration.completion.created_at)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Documents */}
        <DocumentSection
          categories={[
            {
              label: 'Application Documents',
              documents: registration.application_documents || [],
              uploadPath: `/backoffice/tax_registrations/${registration.id}/application_documents`,
              deletePath: `/backoffice/tax_registrations/${registration.id}/application_documents`,
            },
            {
              label: 'Registration Documents',
              documents: registration.registration_documents || [],
              uploadPath: `/backoffice/tax_registrations/${registration.id}/registration_documents`,
              deletePath: `/backoffice/tax_registrations/${registration.id}/registration_documents`,
            },
            {
              label: 'Completion Documents',
              documents: registration.completion_documents || [],
              uploadPath: `/backoffice/tax_registrations/${registration.id}/completion_documents`,
              deletePath: `/backoffice/tax_registrations/${registration.id}/completion_documents`,
            },
          ]}
        />

        {/* Calendar Events */}
        <CalendarEventsSection events={calendar_events || []} />
      </div>
    </BackofficeLayout>
  )
}
